import type { KeyboardEvent } from "react";

import { focusAtEnd, focusAtStart, isCaretAtEnd, isCaretAtStart } from "./caret";

const BRIEF_EDITOR_ROOT_ATTRIBUTE = "data-brief-editor";

function getAdjacentEditable(from: HTMLElement, direction: -1 | 1): HTMLElement | undefined {
    const root = from.closest(`[${BRIEF_EDITOR_ROOT_ATTRIBUTE}]`);
    if (!root) return undefined;
    const editables = [...root.querySelectorAll<HTMLElement>("[contenteditable='true']")];
    const index = editables.indexOf(from);
    if (index < 0) return undefined;
    return editables[index + direction];
}

function focusAdjacentEditable(
    from: HTMLElement,
    direction: -1 | 1,
    edge: "start" | "end",
): boolean {
    const target = getAdjacentEditable(from, direction);
    if (!target) return false;
    if (edge === "start") focusAtStart(target);
    else focusAtEnd(target);
    return true;
}

export function handleTabNavigation<T extends HTMLElement>(event: KeyboardEvent<T>): boolean {
    if (event.nativeEvent.isComposing || event.key !== "Tab") return false;
    const moved = focusAdjacentEditable(event.currentTarget, event.shiftKey ? -1 : 1, "end");
    if (!moved) return false;
    event.preventDefault();
    return true;
}

export function handleArrowNavigation<T extends HTMLElement>(event: KeyboardEvent<T>): boolean {
    if (event.nativeEvent.isComposing) return false;
    if (event.key === "ArrowUp" && isCaretAtStart(event.currentTarget)) {
        const moved = focusAdjacentEditable(event.currentTarget, -1, "end");
        if (!moved) return false;
        event.preventDefault();
        return true;
    }
    if (event.key === "ArrowDown" && isCaretAtEnd(event.currentTarget)) {
        const moved = focusAdjacentEditable(event.currentTarget, 1, "start");
        if (!moved) return false;
        event.preventDefault();
        return true;
    }
    return false;
}

export function handleLinearNavigation<T extends HTMLElement>(event: KeyboardEvent<T>): boolean {
    return handleTabNavigation(event) || handleArrowNavigation(event);
}
